import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { Box, ButtonBase, Chip, Tooltip, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'

export const TILE = 96

export type TileMark = 'new' | 'removed'

export interface PreviewTileProps {
  testId: string
  label: string
  ariaLabel?: string
  selected?: boolean
  /** Absent for a tile that only informs - a removed item is shown, not offered. */
  onClick?: () => void
  dimArtwork?: boolean
  unlocksAt?: number | null
  locked?: boolean
  mark?: TileMark
  children: React.ReactNode
}

export default function PreviewTile({
  testId,
  label,
  ariaLabel,
  selected = false,
  onClick,
  dimArtwork = false,
  unlocksAt = null,
  locked = false,
  mark,
  children,
}: PreviewTileProps) {
  const { formatMessage } = useIntl()

  const frame = {
    width: TILE,
    height: TILE + 28,
    borderRadius: 1,
    border: '2px solid',
    borderColor: selected ? 'primary.main' : 'divider',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    p: 0.5,
    ...(mark === 'removed' ? { borderStyle: 'dashed', opacity: 0.7 } : {}),
  }

  const badge = (side: 'left' | 'right', node: React.ReactNode) => (
    <Box
      sx={{
        position: 'absolute',
        top: 2,
        [side]: 2,
        zIndex: 1,
        '& .MuiChip-root': { height: 18, fontSize: 10 },
        '& .MuiChip-label': { px: 0.5 },
      }}
    >
      {node}
    </Box>
  )

  const body = (
    <>
      <Box sx={{ position: 'relative', width: TILE - 12, flexGrow: 1 }}>
        {/* The img wrapper is what dims: a dimmed label drops below the contrast minimum, and
            the badges carry the meaning anyway. */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: dimArtwork ? 0.45 : 1,
          }}
        >
          {children}
        </Box>
        {mark &&
          badge(
            'left',
            <Tooltip
              title={formatMessage({
                id: `avatarStudio.preview.mark.${mark}Explained`,
              })}
            >
              <Chip
                size="small"
                tabIndex={0}
                color={mark === 'new' ? 'success' : 'warning'}
                data-testid={`${testId}-mark`}
                label={formatMessage({
                  id: `avatarStudio.preview.mark.${mark}`,
                })}
              />
            </Tooltip>,
          )}
        {locked &&
          unlocksAt !== null &&
          badge(
            'right',
            <Tooltip
              title={formatMessage(
                { id: 'avatarStudio.option.unlocksAtExplained' },
                { tier: unlocksAt },
              )}
            >
              <Chip
                size="small"
                tabIndex={0}
                icon={<LockOutlinedIcon sx={{ fontSize: 12 }} />}
                label={formatMessage(
                  { id: 'avatarStudio.option.tierBadge' },
                  { tier: unlocksAt },
                )}
              />
            </Tooltip>,
          )}
      </Box>
      <Typography
        variant="caption"
        noWrap={true}
        translate="no"
        sx={{ maxWidth: TILE - 12 }}
      >
        {label}
      </Typography>
    </>
  )

  if (!onClick) {
    return (
      <Box
        role="group"
        aria-label={ariaLabel ?? label}
        sx={frame}
        data-testid={testId}
      >
        {body}
      </Box>
    )
  }

  return (
    <ButtonBase
      aria-current={selected}
      aria-label={ariaLabel}
      onClick={onClick}
      sx={frame}
      data-testid={testId}
    >
      {body}
    </ButtonBase>
  )
}
